/* eslint-disable prettier/prettier */
import React, { useState, useRef, useEffect } from 'react';
import styles from '../../../../styles/components/pageComponents/program/NewActivityTypeModal.module.css';
import { useActivityTypes } from '../../../../hooks/useActivityType';

const COLOR_PALETTE = [
	'#F97316',
	'#EF4444',
	'#10B981',
	'#3B82F6',
	'#8B5CF6',
	'#EC4899',
	'#640D5F',
	'#EAB308',
];

interface Props {
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
}

export const NewActivityTypeModal: React.FC<Props> = ({
	isOpen,
	onClose,
	onSuccess,
}) => {
	const { createActivityType, loading } = useActivityTypes();

	const [activityName, setActivityName] = useState('');
	const [selectedColor, setSelectedColor] = useState(COLOR_PALETTE[0]);
	const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);

	const colorPickerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (
				colorPickerRef.current &&
				!colorPickerRef.current.contains(e.target as Node)
			) {
				setIsColorPickerOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () =>
			document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	useEffect(() => {
		if (!isOpen) {
			setActivityName('');
			setSelectedColor(COLOR_PALETTE[0]);
			setIsColorPickerOpen(false);
		}
	}, [isOpen]);

	if (!isOpen) return null;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!activityName.trim()) return;

		const response = await createActivityType({
			title: activityName.trim(),
			color: selectedColor,
			displayorder: 0,
		} as any);

		if (response.success) {
			onSuccess?.();
			onClose();
		} else {
			console.error(response.message);
		}
	};

	return (
		<div className={styles.modalOverlay}>
			<div className={styles.modalContainer}>
				{/* Header */}
				<div className={styles.header}>
					<h2 className={styles.title}>New Activity Type</h2>
					<button className={styles.closeBtn} onClick={onClose}>
						✕
					</button>
				</div>

				<form onSubmit={handleSubmit} className={styles.form}>
					<div className={styles.formRow}>
						{/* Color Picker */}
						<div
							className={styles.colorPickerWrapper}
							ref={colorPickerRef}
						>
							<label className={styles.label}>Choose Color</label>

							<button
								type="button"
								className={styles.colorButton}
								onClick={() =>
									setIsColorPickerOpen(!isColorPickerOpen)
								}
							>
								<div
									className={styles.colorBox}
									style={{ backgroundColor: selectedColor }}
								/>
								▼
							</button>

							{isColorPickerOpen && (
								<div className={styles.colorPalette}>
									{COLOR_PALETTE.map(c => (
										<div
											key={c}
											className={styles.colorOption}
											style={{ backgroundColor: c }}
											onClick={() => {
												setSelectedColor(c);
												setIsColorPickerOpen(false);
											}}
										/>
									))}
								</div>
							)}
						</div>

						{/* Input */}
						<div style={{ flex: 1 }}>
							<label className={styles.label}>
								Activity Category Name
							</label>
							<input
								className={styles.input}
								value={activityName}
								onChange={e => setActivityName(e.target.value)}
								placeholder="Add Category Name"
								required
							/>
						</div>
					</div>

					{/* Submit */}
					<button
						type="submit"
						className={styles.submitBtn}
						disabled={loading || !activityName.trim()}
					>
						{loading ? (
							<>
								<div className={styles.spinner} />
								Adding...
							</>
						) : (
							'Add Activity Type'
						)}
					</button>
				</form>
			</div>
		</div>
	);
};
